import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { bindActionCreators } from "redux";
import { ExportFileType } from "../../../../common/enums";
import { appendSearchParamsToURL } from "../../../../common/utils/utils";
import type { UUID } from "../../../../typings/global";
import { AgentSecondaryStatus, AgentStatus } from "../../../agent/enums";
import { PointsReportFilterView } from "../components/PointsReportFilterView";
import { PointsReportTableView } from "../components/PointsReportTableView";
import {
  deleteStatePointsReportAction,
  downloadPointsReportExportActions,
  filterPointsReportActions,
  selectPointsReport
} from "../ducks";
import { PointsReportType } from "../enums";
import type { PointsReportFilterRequest } from "../types";

export const PointsReportContainer = () => {
  const navigate = useNavigate();
  const [urlParams] = useSearchParams();

  const report = useSelector(selectPointsReport);

  const dispatch = useDispatch();
  const actions = useMemo(
    () =>
      bindActionCreators(
        {
          filterPointsReport: filterPointsReportActions.request,
          downloadPointsReportExport: downloadPointsReportExportActions.request,
          deleteStatePointsReport: deleteStatePointsReportAction
        },
        dispatch
      ),
    [dispatch]
  );

  useEffect(() => {
    actions.filterPointsReport({
      periodStartDate: urlParams.get("periodStartDate") ?? undefined,
      periodEndDate: urlParams.get("periodEndDate") ?? undefined,
      institutionIds: urlParams.getAll("institutionIds"),
      productIds: urlParams.getAll("productIds"),
      commissionsSettingsLevelIds: urlParams.getAll("commissionsSettingsLevelIds"),
      commissionsSettingsRankIds: urlParams.getAll("commissionsSettingsRankIds"),
      statuses: urlParams.getAll("statuses") as AgentStatus[],
      secondaryStatuses: urlParams.getAll("secondaryStatuses") as AgentSecondaryStatus[],
      agentCreatedAtDateMin: urlParams.get("agentCreatedAtDateMin") ?? undefined,
      reportType: (urlParams.get("reportType") as PointsReportType) ?? undefined,
      rootAgentId: urlParams.get("rootAgentId") as UUID,
      onlyDirectSubordinates: urlParams.get("onlyDirectSubordinates") === "true" || undefined,
      includeDeactivated: urlParams.get("includeDeactivated") === "true" || undefined,
      includeRepresentatives: urlParams.get("includeRepresentatives") === "true" || undefined,
      includeNonGainers: urlParams.get("includeNonGainers") === "true" || undefined,
      includeWithoutPoints: urlParams.get("includeWithoutPoints") === "true" || undefined
    });

    return () => {
      actions.deleteStatePointsReport();
    };
  }, []);

  const handleFilterSubmit = (filter: PointsReportFilterRequest): void => {
    const urlParams = {
      ...filter,
      reportType: filter.reportType !== PointsReportType.NETTO_AND_BRUTTO_POINTS ? filter.reportType : undefined,
      onlyDirectSubordinates: filter.onlyDirectSubordinates || undefined,
      includeDeactivated: filter.includeDeactivated || undefined,
      includeRepresentatives: filter.includeRepresentatives || undefined,
      includeNonGainers: filter.includeNonGainers || undefined,
      includeWithoutPoints: filter.includeWithoutPoints || undefined
    } as PointsReportFilterRequest;

    navigate(appendSearchParamsToURL(urlParams as Record<string, any>), { replace: true });

    actions.filterPointsReport(urlParams);
  };

  const handleReportExportClick = (exportFileType: ExportFileType): void => {
    const {
      periodStartDate,
      periodEndDate,
      institutionIds,
      productIds,
      commissionsSettingsLevelIds,
      commissionsSettingsRankIds,
      statuses,
      secondaryStatuses,
      agentCreatedAtDateMin,
      reportType,
      rootAgentId,
      onlyDirectSubordinates,
      includeDeactivated,
      includeRepresentatives,
      includeNonGainers,
      includeWithoutPoints
    } = report;

    actions.downloadPointsReportExport({
      periodStartDate,
      periodEndDate,
      institutionIds,
      productIds,
      commissionsSettingsLevelIds,
      commissionsSettingsRankIds,
      statuses,
      secondaryStatuses,
      agentCreatedAtDateMin,
      reportType,
      rootAgentId,
      onlyDirectSubordinates,
      includeDeactivated,
      includeRepresentatives,
      includeNonGainers,
      includeWithoutPoints,
      exportFileType
    });
  };

  return (
    <>
      <PointsReportFilterView filter={report} onFilterSubmit={handleFilterSubmit} />

      <PointsReportTableView report={report} onExportClick={handleReportExportClick} />
    </>
  );
};
